import { useCallback, useEffect, useState, useMemo } from "react";
import { useFormContext } from "../../../../common/contexts/FormContext";
import { mapZoomLevel, sharedStateKeys } from "../../../../constants/constant";
import { hereMapApiKey } from "../../../../service/apiConfig";
import InteractiveMap from "./InteractiveMap";
import {
  getPropertyLawnAreaCoordinates,
  redirectToConfirmationWithError,
} from "./coordinate";
import { convertGeoJSONToPropertyAreas } from "./util";
import { useFormConfigContext } from "../../../../common/contexts/FormConfigContext";

import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";

interface PropertyConfirmationProps {
  handlePropertySelectedArea: (selectedAreas: {
    totalSelectedArea: string;
  }) => void;
  onToggleLeftSidebar?: () => void;
  deepLawnPolygon?: boolean;
  EnableFrontAndBackLawn?: boolean;
}

const PropertyConfirmation = ({
  handlePropertySelectedArea,
  onToggleLeftSidebar,
  deepLawnPolygon = false,
  EnableFrontAndBackLawn = false,
}: PropertyConfirmationProps) => {
  const { formValues, setFormValues } = useFormContext();
  const { formConfig } = useFormConfigContext();
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [center, setCenter] = useState<[number, number]>([40.7128, -74.006]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { setValue } = useSharedStateWithActions();
  const [lawnImgMap, setLawnImgMap] = useState<boolean>(false);

  useEffect(() => {
    const loadPropertyArea = async () => {
      try {
        const coordinatesData = await getPropertyLawnAreaCoordinates(
          formValues,
          formConfig,
          EnableFrontAndBackLawn,
        );
        if (
          !coordinatesData?.geojson ||
          (typeof coordinatesData.geojson === "object" &&
            Object.keys(coordinatesData.geojson).length === 0)
        ) {
          const { squareFeet } = coordinatesData || {};
          const frontYardArea = Number(squareFeet?.FRONT_YARD ?? 0);
          const backYardArea = Number(squareFeet?.BACK_YARD ?? 0);
          const combinedLawnArea = frontYardArea + backYardArea;

          if (combinedLawnArea === 0) {
            handlePropertySelectedArea({ totalSelectedArea: "0" });
            redirectToConfirmationWithError(formValues, formConfig);
            return;
          }
          setValue(sharedStateKeys.IS_INTERACTIVE_MAP_READY, true);
          handlePropertySelectedArea({
            totalSelectedArea: combinedLawnArea.toString(),
          });
          setLawnImgMap(true);
        } else {
          setLawnImgMap(false);
          setValue(sharedStateKeys.IS_INTERACTIVE_MAP_READY, true);
          const lawnOptions = EnableFrontAndBackLawn
            ? { frontLawn: true, backLawn: true }
            : { lawn: true };

          const areas = convertGeoJSONToPropertyAreas(
            coordinatesData?.geojson,
            deepLawnPolygon,
            lawnOptions,
          );

          if (areas.length === 0) {
            setLawnImgMap(true);
            redirectToConfirmationWithError(formValues, formConfig);
            return;
          }
          const areasWithSelection = areas.map((area) => ({
            ...area,
            selected: true,
          }));

          const allAreasHaveZeroSize = areasWithSelection.every(
            (area) => !area.size || area.size === 0,
          );

          if (allAreasHaveZeroSize && areasWithSelection.length > 0) {
            // Call handler with 0 before redirecting
            handlePropertySelectedArea({ totalSelectedArea: "0" });
            redirectToConfirmationWithError(formValues, formConfig);
            return;
          }

          setPropertyAreas(areasWithSelection);

          if (areasWithSelection.length > 0) {
            const allCoordinates = areasWithSelection.flatMap(
              (area) => area.coordinates,
            );
            const centerLat =
              allCoordinates.reduce((sum, coord) => sum + coord[0], 0) /
              allCoordinates.length;
            const centerLng =
              allCoordinates.reduce((sum, coord) => sum + coord[1], 0) /
              allCoordinates.length;
            setCenter([centerLat, centerLng]);
          }
        }
      } catch (error) {
        console.error("Error loading property area:", error);
        redirectToConfirmationWithError(formValues, formConfig);
      } finally {
        setIsLoading(false);
      }
    };
    loadPropertyArea();
  }, []);

  const handleAreaSelect = useCallback(
    (totalSelectedArea: number) => {
      setFormValues((prevValues) => ({
        ...prevValues,
        "estimate-lawn-size": totalSelectedArea,
      }));
      const formattedSize = totalSelectedArea.toLocaleString();
      handlePropertySelectedArea({ totalSelectedArea: formattedSize });
    },
    [handlePropertySelectedArea, setFormValues],
  );

  const memoizedHereConfig = useMemo(
    () => ({
      apiKey: hereMapApiKey[process.env.NODE_ENV || "development"],
    }),
    [],
  );

  return (
    <div>
      {isLoading && (
        <div className="map-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      {lawnImgMap ? (
        <img
          src={`${window.location.origin}/us/en-us/_assets/images/map.jpg`}
          alt="Map Background"
          className="map-background"
          style={{ margin: "10px  0", width: "100%", height: "100%" }}
        />
      ) : (
        <InteractiveMap
          center={center}
          zoom={mapZoomLevel}
          propertyAreas={propertyAreas}
          onAreaSizeCalculate={handleAreaSelect}
          hereConfig={memoizedHereConfig}
          onToggleLeftSidebar={onToggleLeftSidebar}
        />
      )}
    </div>
  );
};

export default PropertyConfirmation;
